import React, { useState } from 'react';
import Modal from './Modal';
import Button from './Button';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  content: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
  type?: 'warning' | 'danger' | 'info';
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  content,
  onConfirm,
  onCancel,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'warning'
}) => {
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
    } finally {
      setLoading(false);
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'danger':
      case 'warning':
        return <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />;
      default:
        return <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" />;
    }
  };

  const getConfirmButtonVariant = () => {
    switch (type) {
      case 'danger':
        return 'danger' as const;
      default:
        return 'primary' as const;
    }
  };

  const footer = (
    <>
      <Button variant="outline" onClick={onCancel} disabled={loading}>
        {cancelText}
      </Button>
      <Button 
        variant={getConfirmButtonVariant()} 
        onClick={handleConfirm} 
        loading={loading}
      >
        {confirmText}
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={title}
      footer={footer}
      size="sm"
    >
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          {getIcon()}
        </div>
        <div className="flex-1">
          <p className="text-sm text-gray-700">{content}</p>
        </div>
      </div>
    </Modal>
  );
};

// Utility function to show confirmation dialogs
interface ConfirmOptions {
  title: string;
  content: string;
  onConfirm: () => void | Promise<void>;
  confirmText?: string;
  cancelText?: string;
  type?: 'warning' | 'danger' | 'info';
}

export const showConfirm = (options: ConfirmOptions) => {
  return new Promise<boolean>((resolve) => {
    const handleConfirm = async () => {
      try {
        await options.onConfirm();
        resolve(true);
      } catch (error) {
        resolve(false);
      }
    };

    const handleCancel = () => {
      resolve(false);
    };

    // This would need to be integrated with a modal provider in a real implementation
    // For now, we'll just call the onConfirm directly
    if (window.confirm(`${options.title}\n\n${options.content}`)) {
      handleConfirm();
    } else {
      handleCancel();
    }
  });
};

export default ConfirmDialog; 